import React from "react";
import { UserPlus } from "lucide-react";
import toast from "react-hot-toast";
import { PlayerGallery } from "./PlayerGallery";
import ScoutingReportManager from "../../managers/ScoutingReportsManager";

export const ScoutingView = ({
  report,
  onUpdateReport,
  onAddPlayer,
  onAddPitcher,
  onEditPlayer,
  onEditPitcher,
  onViewPlayer,
  onViewPitcher
}) => {

  const handleDeletePlayer = async (playerId) => {
    const playerToDelete = report.positionPlayers.find((p) => p.id === playerId);

    toast(
      (t) => (
        <div className="flex flex-col gap-4">
          <p>Delete {playerToDelete.name}?</p>
          <div className="flex gap-2">
            <button
              className="px-3 py-2 text-sm text-white bg-red-500 rounded hover:bg-red-600"
              onClick={async () => {
                const loadingToast = toast.loading("Deleting player...");
                try {
                  const updatedReport = {
                    ...report,
                    positionPlayers: report.positionPlayers.filter(
                      (p) => p.id !== playerId
                    ),
                    numHitters: report.numHitters - 1,
                  };
                  await ScoutingReportManager.updateReport(
                    report.id,
                    updatedReport
                  );
                  onUpdateReport(updatedReport);
                  toast.success("Player deleted successfully", {
                    id: loadingToast,
                  });
                } catch (error) {
                  console.error("Error deleting player:", error);
                  toast.error("Failed to delete player", { id: loadingToast });
                }
                toast.dismiss(t.id);
              }}
            >
              Delete
            </button>
            <button
              className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
              onClick={() => toast.dismiss(t.id)}
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      { duration: 5000 }
    );
  };

  const handleDeletePitcher = async (pitcherId) => {
    const pitcherToDelete = report.pitchers.find((p) => p.id === pitcherId);

    toast(
      (t) => (
        <div className="flex flex-col gap-4">
          <p>Delete {pitcherToDelete.name}?</p>
          <div className="flex gap-2">
            <button
              className="px-3 py-2 text-sm text-white bg-red-500 rounded hover:bg-red-600"
              onClick={async () => {
                const loadingToast = toast.loading("Deleting pitcher...");
                try {
                  const updatedReport = {
                    ...report,
                    pitchers: (report.pitchers || []).filter(
                      (p) => p.id !== pitcherId
                    ),
                    numPitchers: report.numPitchers - 1,
                  };
                  await ScoutingReportManager.updateReport(
                    report.id,
                    updatedReport
                  );
                  onUpdateReport(updatedReport);
                  toast.success("Pitcher deleted successfully", {
                    id: loadingToast,
                  });
                } catch (error) {
                  console.error("Error deleting pitcher:", error);
                  toast.error("Failed to delete pitcher", { id: loadingToast });
                }
                toast.dismiss(t.id);
              }}
            >
              Delete
            </button>
            <button
              className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
              onClick={() => toast.dismiss(t.id)}
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      { duration: 5000 }
    );
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Content */}
      <div className="p-6">
        {/* Position Players Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900">Hitters</h3>
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                {report.positionPlayers?.length || 0}
              </span>
            </div>
            <button
              onClick={onAddPlayer}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <UserPlus size={14} />
              Add
            </button>
          </div>

          <PlayerGallery
            players={report.positionPlayers}
            onEditPlayer={onEditPlayer}
            onViewPlayer={onViewPlayer}
            onDeletePlayer={handleDeletePlayer}
            isPitcherGallery={false}
          />
        </div>

        {/* Pitchers Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900">Pitchers</h3>
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                {report.pitchers?.length || 0}
              </span>
            </div>
            <button
              onClick={onAddPitcher}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
            >
              <UserPlus size={14} />
              Add
            </button>
          </div>

          <PlayerGallery
            players={report.pitchers || []}
            onEditPlayer={onEditPitcher}
            onViewPlayer={onViewPitcher}
            onDeletePlayer={handleDeletePitcher}
            isPitcherGallery={true}
          />
        </div>
      </div>
    </div>
  );
};
